import React, { useEffect, useState, useMemo, use } from "react";
import {
  DatePicker,
  Input,
  Select,
  Button,
  Breadcrumb,
  message,
  Modal,
  ConfigProvider,
} from "antd";
import dayjs from "dayjs";
import { DoubleRightOutlined } from "@ant-design/icons";
import { SetHeading } from "../../context/NavbarContext";
import i18next from "i18next";

import { DashboardShimmer } from "../../loading"
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { BarChart, barElementClasses } from "@mui/x-charts/BarChart";
import { PieChart } from "@mui/x-charts/PieChart";

import { axisClasses } from "@mui/x-charts/ChartsAxis";

import {formatCompactNumber} from "../../Utils/formatCompactNumber"
import Box from "@mui/material/Box"
import Typography from "@mui/material/Typography"
import {
  getExpenseTypes,
  createExpenseType,
  createExpenseEntry,
  getExpenseTransactions,
  editOrDeleteExpenseEntry,
  getExpensePaymentMethods,
  fetchMethodBalance,
} from "../../API  Functions/ExpenseManagementFunctions";
import ExpenseTransactionTable from "./ExpenseTransactionTable";
import SelectBox from "../CustomWidgets/SelectBox";
import moment from "moment";
import { toast } from "react-toastify";
import EditExpenseModal from "./EditExpenseModal";
import DateFilter from "../ui/DateFilter";
import { checkPermission } from "../../Utils/permissionHelper";
import axiosInstance from "../../AxiosInstance";
import { URLS } from "../../API/API endpoints";

const { Option } = Select;

function CustomLegend({ items, dir = "rtl" }) {
  return (
    <div
      dir={dir}
      style={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: 16,
        marginBottom: 8,
        fontFamily: "Cairo, sans-serif",
      }}
    >
      {items.map((item) => (
        <div
          key={item.label}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <span
            style={{
              width: 12,
              height: 12,
              borderRadius: 2,
              backgroundColor: item.color,
              flexShrink: 0,
            }}
          />
          <span
            style={{
              fontSize: 12,
              fontWeight: 500,
              color: "#333",
            }}
          >
            {item.label}
          </span>
        </div>
      ))}
    </div>
  );
}

const ExpenseTracker = () => {
  const { t, i18n } = useTranslation();
  const userId = useSelector((state) => state?.login?.user?.result?.user_id);
  const companyId = useSelector(
    (state) => state?.login?.user?.result?.company_id,
  );

  const isGinne = useSelector((state) => state?.geneLoginReducer?.isGinneLogin);

  const [expenseDate, setExpenseDate] = useState(null);
  const [expenseType, setExpenseType] = useState("");
  const [filterExpenseType, setFilterExpenseType] = useState("")
  const [amount, setAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [remarks, setRemarks] = useState("");
  const [dashLoading, setDashLoading] = useState(false)

  const [expenseTypes, setExpenseTypes] = useState([]);
  const [expensePaymentMethods, setExpensePaymentMethods] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [totalAmount, setTotalAmount] = useState(0)
  const [transactions, setTransactions] = useState([]);
  const [dateRange, setDateRange] = useState([null, null]);

  const [selectedRange, setSelectedRange] = useState("thisMonth");

  const [loading, setloading] = useState(false);
  const [balanceLoading, setBalanceLoading] = useState(false);

  const [editingExpense, setEditingExpense] = useState(null);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [currentLimit, setCurrentLimit] = useState(50); // Default limit
  const [currentOffset, setCurrentOffset] = useState(0); // Default offset

  const [saving, setSaving] = useState(false);
  const today = dayjs();
  const formatToday = today.format("YYYY-MM-DD");
  const startofMonth = today.startOf("month").format("YYYY-MM-DD");

  const [startDate, setStartDate] = useState(startofMonth);
  const [endDate, setEndDate] = useState(formatToday);
  const [chartStartDate, setChartSetDate] = useState(startofMonth)
  const [chartEndDate, setChartEndDate] = useState(formatToday);
  const [balance, setBalance] = useState(null);

  const pieColors = [
    "#F2994A", "#2F80ED", "#27AE60", "#EB5757", "#9B51E0",
    "#F2C94C", "#56CCF2", "#BB6BD9", "#219653", "#828282",
  ];

  const chartSetting = {
    margin: { left: 90, right: 100 }, // must be >= yAxis.width + label height + a little buffer
    borderRadius: 10,
    sx: {
      "& .MuiBarLabel-root": {
        fill: "#fff",
        fontWeight: 600,
        fontSize: 12,
      },
      [`.${axisClasses.left} .${axisClasses.label}`]: {
        // this is fine-tuning ON TOP of the reserved width, not a substitute for it
        transform: "translateX(-15px)",
      },
    },
  };

  const allStores = useSelector(
    (state) => state?.storeReducer?.selectedStore?.is_select_all_store,
  );

  const [chartData, setChartData] = useState([])
  const pieData = useMemo(() => {
    // 1. Normalize to positive values
    const cleaned = (chartData ?? [])
      .filter(d => d.type?.trim())
      .map(d => ({
        type: t(d.type),
        amount: Math.abs(Number(d.amount)),
      }))
      .filter(d => Number.isFinite(d.amount) && d.amount > 0);

    if (!cleaned.length) return [];

    // 2. Top N + Other
    const sortedDesc = [...cleaned].sort((a, b) => b.amount - a.amount);

    const top = sortedDesc.slice(0, 7);
    const rest = sortedDesc.slice(7);

    const restSum = rest.reduce((sum, d) => sum + d.amount, 0);

    const entries = [...top];

    if (restSum > 0) {
      entries.push({
        type: t("Other"),
        amount: restSum,
      });
    }

    const total = entries.reduce((sum, d) => sum + d.amount, 0);

    return entries.map((d, i) => ({
      id: d.type,
      label: `${d.type} (${((d.amount / total) * 100).toFixed(1)}%)`,
      value: d.amount,
      color: pieColors[i % pieColors.length],
    }));
  }, [chartData, t]);

  const data = chartData
    .map(item => ({
      type: t(item.type),
      amount: Math.abs(item.amount),
    }))
    .sort((a, b) => b.amount - a.amount)
    .slice(0, 10);

  const barchartData = useMemo(() => {
    return (data ?? [])
      .filter(d => d.type?.trim() && Number.isFinite(Number(d.amount)))
      .map(d => ({ ...d, amount: Number(d.amount) }));
  }, [chartData]);

  console.log("BARCHART DATA", barchartData)

  // Fetch expense types
  const fetchExpenseTypes = async () => {
    try {
      setLoadingTypes(true);
      const res = await getExpenseTypes(companyId);
      const types = res?.result?.expense_types?.map((type) => type.name) || [];
      setExpenseTypes(types);
      setLoadingTypes(false);
    } catch (err) {
      console.error(err);
      setLoadingTypes(false);
    }
  };

  const fetchExpensePaymentMethods = async () => {
    try {
      setLoadingTypes(true);
      const res = await getExpensePaymentMethods(companyId);
      // const types = res?.result?.expense_types?.map((type) => type.name) || [];
      setExpensePaymentMethods(res?.result?.data);
      setLoadingTypes(false);
    } catch (err) {
      console.error(err);
      setLoadingTypes(false);
    }
  };

  const fetchExpenseBarChart = async (start, end) => {
    try {
      setDashLoading(true);

      let lang = i18next.language;

      if (lang === "ar") {
        lang = "ar_001";
      } else if (lang === "en") {
        lang = "en_US";
      }

      const payload = {
        company_id: companyId,
        ...(start !== "" && { start_date: start }),
        ...(end !== "" && { end_date: end }),
        lang

      };

      const res = await axiosInstance.post(
        URLS.EXPENSE_BAR_CHART,
        payload
      );

      console.log(res?.data?.result?.chart_data);

      setChartData(res?.data?.result?.chart_data);
    } catch (err) {
      console.error(err);
    } finally {
      setDashLoading(false);
    }
  };

  const fetchTransactions = async (
    start_date,
    end_date,
    limit,
    offset,
    expenseType = filterExpenseType
  ) => {
    try {
      const res = await getExpenseTransactions({
        user_id: userId,
        company_id: companyId,
        start_date,
        end_date,
        limit,
        offset,
        is_expense: true,
        expense_type: expenseType,
      });

      if (res?.result?.status === "success") {
        setTotalAmount(res.result.total_amount)
        setTransactions(res.result.transactions || []);
      } else {
        message.error(t("Failed to fetch transactions"));
      }
    } catch (err) {
      console.error(err);
      message.error(t("Failed to fetch transactions"));
    }
  };

  useEffect(() => {
    fetchExpenseTypes();
    fetchExpensePaymentMethods();
    const today = dayjs().format("YYYY-MM-DD");
    fetchTransactions(startDate, endDate, currentLimit, currentOffset);
  }, [currentLimit, currentOffset]);

  useEffect(() => {
    fetchExpenseBarChart("", "")
  }, [])

  useEffect(() => {
    if (chartStartDate && chartEndDate) {
      fetchExpenseBarChart(chartStartDate, chartEndDate);
    }
  }, [chartStartDate, chartEndDate]);



  const resetForm = () => {
    setExpenseDate(null);
    setExpenseType("");
    setAmount("");
    setPaymentMethod("");
    setRemarks("");
    setBalance(null)
  };

  const fetchBalance = async (selectedType) => {
    if (!selectedType) return;

    try {
      setBalanceLoading(true);
      let payload = {
        store_ids: [companyId],
        from_type: selectedType,
      };

      const res = await fetchMethodBalance(payload);
      if (res?.result?.status_code === 200) {
        const balanceAmt = parseFloat(res?.result?.balance || 0);
        setBalance(balanceAmt);
      } else {
        setBalance(null);
        message.error(t("Failed to fetch balance"));
      }
    } catch (err) {
      console.error(err);
      setBalance(null);
    } finally {
      setBalanceLoading(false);
    }
  };

  const handleSave = async () => {
    if (!expenseDate || !expenseType || !amount) {
      message.warning(t("Please fill all required fields"));
      return;
    }

    if (saving) return; // Prevent multiple clicks

    setSaving(true);

    let typeName = expenseType;

    try {
      // Create new expense type if needed
      if (!expenseTypes.includes(expenseType)) {
        const createdType = await createExpenseType(companyId, expenseType);
        if (!createdType) return; //  This return skips setSaving(false)
        typeName = createdType.result?.expense_types?.[0]?.name || expenseType;
        await fetchExpenseTypes();
      }

      // Create expense entry
      await createExpenseEntry({
        company_id: companyId,
        user_id: userId,
        amount: parseFloat(amount),
        remark: remarks,
        expense_type: typeName,
        expense_date: dayjs(expenseDate).format("YYYY-MM-DD"),
        payment_method: paymentMethod,
      });

      message.success(t("Expense saved successfully"));
      resetForm();
      setSelectedRange("thisMonth");
      setStartDate(startofMonth);
      setEndDate(formatToday);
      fetchTransactions(startofMonth, formatToday, 50, 0);
    } catch (err) {
      console.error(err);
      message.error(t("Failed to save expense"));
    } finally {
      setSaving(false); // Always reset saving, even after errors/returns
    }
  };

  const disabledExpenseDate = (current) => {
    return current && current > dayjs().endOf("day");
  };

  // Check if all fields are empty (for Cancel button)
  const isFormEmpty =
    !expenseDate && !expenseType && !amount && !paymentMethod && !remarks;
  const isCancelDisable = !expenseDate && !expenseType && !amount;
  // Check if required fields are filled (for Save button)
  const isSaveDisabled = !expenseDate || !expenseType || !amount;

  const handleEdit = (record) => {
    console.log("fhrfhesjsdg", record);

    setEditingExpense(record);
    setEditModalOpen(true);
  };

  const handleDelete = async (record) => {
    try {
      const payload = {
        user_id: userId,
        company_id: companyId,
        record_id: record.id,
        is_delete: true,
      };

      const res = await editOrDeleteExpenseEntry(payload);
      // console.log("DDDD", res);

      if (res?.result?.status_code === 200) {
        toast.success(t("Expense deleted successfully"));
        fetchTransactions(startDate, endDate, currentLimit, currentOffset);
      } else {
        message.error("Failed to delete expense");
      }
    } catch (err) {
      message.error("Error deleting expense");
    }
  };

  const handleEditClick = (expense) => {
    setSelectedExpense(expense);
    setEditModalOpen(true);
  };


  return (
    <div className="flex w-full flex-col overflow-y-auto bg-[#F6F7F9] pb-2 md:h-full">
      <SetHeading value={t("Expense Management")} />
      <div className="m-4 md:m-0 md:mx-5 overflow-y-auto custom-scrollbar">
        {/* Add Expense Form */}
        <div className=" md:my-3 md:me-2">
          <h3 className="font-bold mb-4">{t("Add Expense")}</h3>
          <div className="grid grid-cols-1 gap-2 gap-y-4 md:gap-y-4 mb-4 sm:grid-cols-2 sm:gap-8">
            {/* Expense Date */}
            <div className="flex flex-col">
              <label className="text-xs md:text-sm mb-1">
                {t("Expense Date")}
              </label>
              <DatePicker
                style={{ width: "100%" }}
                placeholder={t("Select Date")}
                value={expenseDate}
                onChange={(date) => setExpenseDate(date)}
                className="h-10 "
                disabledDate={disabledExpenseDate} // <- Prevent future dates
              />
            </div>

            {/* Expense Type */}
            <div className="flex flex-col">
              <label className="text-xs md:text-sm mb-1">
                {t("Expense Type")}
              </label>
              <Select
                style={{ width: "100%" }}
                placeholder={t("Select or Create Expense Type")}
                value={t(expenseType)}
                onChange={(value) => setExpenseType(value)}
                onSearch={(value) => setExpenseType(value)}
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
                notFoundContent={
                  loadingTypes ? t("Loading...") : t("Type to create new")
                }
                className="h-10 "
                dropdownRender={(menu) => (
                  <>
                    {menu}
                    <div className="p-2 border-t flex">
                      <Input
                        placeholder={t("Enter new expense type")}
                        value={expenseType}
                        onChange={(e) => setExpenseType(e.target.value)}
                        className="flex-1 h-8"
                      />
                      <Button
                        type="link"
                        onClick={() =>
                          message.info(
                            t("New expense type will be created on Save"),
                          )
                        }
                      >
                        {t("Create")}
                      </Button>
                    </div>
                  </>
                )}
              >
                {expenseTypes.map((type) => (
                  <Option key={type} value={type}>
                    {t(type)}
                  </Option>
                ))}
              </Select>
            </div>


            <div className="flex flex-col">
              <label className="text-xs md:text-sm mb-1">
                {t("Payment Method")}
              </label>

              <Select
                style={{ width: "100%" }}
                value={paymentMethod}
                onChange={(value) => {
                  setPaymentMethod(value);
                  fetchBalance(value);
                }}
                className="h-10"
              >
                {expensePaymentMethods?.map((item) => (
                  <Option key={item.name} value={item.name}>
                    {t(item.name)}
                  </Option>
                ))}
              </Select>
            </div>

            {/* Amount */}
            <div className="flex flex-col">
              <label className="text-xs md:text-sm mb-1">{t("Amount")}</label>
              {/* <Input
                type="number"
                placeholder={t("Enter Amount")}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="h-10 "
              /> */}
              <Input
                type="text"
                placeholder={t("Enter Amount")}
                value={amount}
                onChange={(e) => {
                  const value = e.target.value;

                  // Allow only digits and optional one decimal point (not at start)
                  const regex = /^(?!0\d)(\d+(\.\d{0,2})?)?$/;

                  // ✅ Allow empty value, or valid numeric input
                  if (value === "" || regex.test(value)) {
                    // Prevent leading '.' or '-'
                    if (value.startsWith(".") || value.startsWith("-")) return;
                    setAmount(value);
                  }
                }}
                inputMode="decimal"
                maxLength={10}
                className="h-10"
              />
              {balanceLoading ? (
                <p className="text-sm text-gray-500 mt-1">
                  {t("Fetching balance...")}
                </p>
              ) : balance !== null ? (
                <p
                  className={`text-sm italic mt-1 
                
                  `}
                >
                  {t("Available Balance")}: {balance.toFixed(2)}
                </p>
              ) : null}
            </div>

            {/* Payment Method */}


            {/* Remarks */}
            <div className="flex flex-col ">
              <label className="text-xs md:text-sm mb-1">{t("Remarks")}</label>
              <Input.TextArea
                placeholder={t("Enter Remarks")}
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                className=""
              />
            </div>
          </div>

          {/* Save Button */}
          <div className=" flex gap-3 flex-row justify-end">
            <button
              onClick={resetForm}
              className={`w-full sm:w-auto px-4 py-2 font-semibold text-sm border rounded-lg
            ${isCancelDisable
                  ? "bg-white text-primaryBlue border-primaryBlue hover:bg-red-600 opacity-70 cursor-not-allowed"
                  : "bg-white text-primaryBlue border-primaryBlue hover:bg-red-600"
                }`}
              disabled={isCancelDisable}
            >
              {t("Cancel")}
            </button>
            <button
              // onClick={handleSave}
              onClick={() => {
                if (checkPermission("/settings/expense", isGinne)) {
                  handleSave();
                }
              }}
              className={`w-full sm:w-auto px-4 py-2 font-semibold text-sm border rounded-lg
    ${isSaveDisabled || saving
                  ? "bg-primaryBlue text-white border-primaryBlue opacity-70 cursor-not-allowed"
                  : "bg-primaryBlue text-white border-primaryBlue hover:bg-red-600"
                }`}
              disabled={isSaveDisabled || saving} // 🔹 Disable when saving
            >
              {saving ? t("Saving...") : t("Save")}
            </button>
          </div>
        </div>

        {/* {transactions?.length < 1 && selectedRange == "thisMonth" ? (
          <></>
        ) : (
          <> */}
        <div className="flex gap-5 mt-4 md:mt-0">
          <DateFilter
            setSelectedRange={setSelectedRange}
            selectedRange={selectedRange}
            SubmitFilter={(start, date) => {
              setStartDate(start);
              setEndDate(date);
              fetchTransactions(start, date, 50, 0);
              fetchExpenseBarChart(start, date)
            }}
          />


        </div>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 3,
            width: "100%",
            height: { xs: "auto", md: 440 },
            borderRadius: 2,
            marginTop: 2
          }}
        >
          {/* Bar chart */}
          {data?.length > 0 && (
            <Box
              sx={{
                flex: 2,
                minWidth: 280,
                height: 440,
                backgroundColor: "#ffffff",
                border: "1px solid #e0e0e0",
                borderRadius: 2,
                padding: 2,
                boxSizing: "border-box",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontFamily: "Cairo, sans-serif",
                  fontWeight: 600,
                  fontSize: 15,
                  color: "#333",
                  mb: 1,
                  textAlign: "center",
                }}
              >
                {t("Expenses by Type")}
              </Typography>

              <div dir="ltr" className="overflow-x-auto">
                <div style={{ minWidth: Math.max(600, barchartData.length * 120) }}>
                  <BarChart
                    dataset={barchartData}
                    layout="vertical"
                    barLabel={(item) => {
    console.log("BAR LABEL ITEM:", item);

    if (item.value == null) return "";

    return formatCompactNumber(item.value);
  }}

                    xAxis={[
                      {
                        scaleType: "band",
                        dataKey: "type",
                        tickLabelStyle: {
                          fontSize: 12,
                          fontFamily: "Cairo, sans-serif",
                        },
                        categoryGapRatio: 0.4,
                      },
                    ]}
                    yAxis={[
                      {
                        scaleType: "linear",
                        dataKey: "amount",
                        label: t("Sales Value(EGP)"),
                        labelStyle: {
                          fontSize: 12,
                          fontFamily: "Cairo, sans-serif",
                          fontWeight: 600,
                        },
                        tickLabelStyle: {
                          fontSize: 11,
                        },
                        valueFormatter: (value) =>
                          new Intl.NumberFormat("en", { notation: "compact" }).format(value),
                        width: 90,
                      },
                    ]}


                    series={[

                      {
                        dataKey: "amount",
                        label: t("Expense"),
                        color: "#F2994A",
                        valueFormatter: (value) =>{
                          console.log("VALUE",value)
                          return (
                          value == null
                            ? ""
                            :  `EGP ${value.toLocaleString()} `)},
                      },

                    ]}
                    height={360}
                    width={Math.max(600, barchartData.length * 120)}
                    {...chartSetting}
                  />
                </div>
              </div>
            </Box>
          )}

          {/* Pie chart */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              width: "100%",
              border: "1px solid #e0e0e0",
              borderRadius: 2,
              height: 440,
              backgroundColor: "#ffffff",
              padding: 2,
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              overflow: "visible",
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                fontFamily: "Cairo, sans-serif",
                fontWeight: 600,
                fontSize: 15,
                color: "#333",
                mb: 1,
                textAlign: "center",
              }}
            >
              {t("Expense Distribution")}
            </Typography>

            {pieData.length > 0 ? (
              (() => {
                const total = pieData.reduce((sum, item) => sum + item.value, 0);

                return (
                  <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                    <PieChart
                      series={[

                        {
                          valueFormatter: (item) => {
                            console.log("PIE ITEM", item);
                            return `EGP ${item.value.toLocaleString()} `;
                          },
                          data: pieData.map((item) => ({
                            ...item,
                          })),

                          arcLabel: (item) => {
                            if (!total) return "";
                            const percentage = (item.value / total) * 100;
                            return `${Math.round(percentage)}%`;
                          },

                          arcLabelMinAngle: 15,
                          innerRadius: 50,
                          outerRadius: 100,
                          cornerRadius: 3,

                          highlightScope: {
                            fade: "global",
                            highlight: "item",
                          },
                        },
                      ]}

                      height={260}
                      width={Math.min(420, 320)}
                      margin={{ top: 10, bottom: 10, left: 0, right: 0 }}
                      skipAnimation

                      slotProps={{
                        legend: {
                          hidden: true,
                        },
                        pieArcLabel: {
                          style: {
                            fontFamily: "Cairo, sans-serif",
                            fontSize: 12,
                            fontWeight: 600,
                            fill: "#fff",
                          },
                        },
                      }}
                    />
                    {/* ✅ Custom HTML legend — scrollable, wraps properly */}
                    <Box
                      sx={{
                        width: "100%",
                        maxHeight: 100,          // cap the legend area
                        display: "flex",
                        flexWrap: "wrap",
                        justifyContent: "center",
                        gap: "6px 14px",
                        px: 1,
                        mt: 1,
                      }}
                    >
                      {pieData.map((item) => (
                        <Box
                          key={item.id}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            fontFamily: "Cairo, sans-serif",
                            fontSize: 12,
                            fontWeight: 500,
                            color: "#333",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: "3px",
                              backgroundColor: item.color,
                              flexShrink: 0,
                            }}
                          />
                          {item.label}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                );
              })()
            ) : (
              <p className="text-gray-400 text-sm">{t("No data available")}</p>
            )}
          </Box>
        </Box>

        <div className="flex flex-col mt-4 mb-2 cursor-pointer">
          <Select
            style={{ width: "280px", alignContent: "center", height: "40px" }}
            placeholder={t("Select Expense Type")}
            value={filterExpenseType || "all"}
            onChange={(value) => {
              console.log("SELECT onChange raw value:", value);

              setFilterExpenseType(value === "all" ? "" : value);

              fetchTransactions(
                startDate,
                endDate,
                50,
                0,
                value === "all" ? "" : value
              );
            }}
            filterOption={(input, option) =>
              option.children.toLowerCase().includes(input.toLowerCase())
            }
          >
            <Option value="all">
              {t("All")}
            </Option>

            {expenseTypes.map((type) => (
              <Option key={type} value={type}>
                {t(type)}
              </Option>
            ))}
          </Select>
          {filterExpenseType !== "" &&
            <div className=" mt-4">
              <div className="font-semibold text-sm text-primaryBlue">
                {t("Total Amount :")} EGP {totalAmount ? totalAmount.toFixed(2) : "0"}
              </div>
            </div>
          }
        </div>

        <ExpenseTransactionTable
          additionalData={transactions}
          onEdit={handleEdit}
          onDelete={handleDelete}
          pagination={(values) => {
            setCurrentLimit(values.limit);
            setCurrentOffset(values.offset);
          }}
        />
        {/* </>
        )} */}

        <EditExpenseModal
          isOpen={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          expense={editingExpense}
          onUpdate={() => fetchTransactions(startDate, endDate, 50, 0)} // ✅ Refresh list
        />
      </div>
    </div>
  );
};

export default ExpenseTracker;
